<div class="parking-right-add-dialog">
    <h2 mat-dialog-title class="flex items-center gap-2">
        <mat-icon class="text-green-600">add_circle</mat-icon>
        Add Parking Right
    </h2>

    <mat-dialog-content>
        <mat-stepper [linear]="true" #stepper class="parking-right-stepper">

            <!-- ==================== STEP 1 — KIND ==================== -->
            <mat-step [completed]="true">
                <ng-template matStepLabel>Kind</ng-template>

                <label class="block text-sm font-medium text-gray-700 mb-2 mt-2">{{ 'parkingRight.add.kindQuestion'
                    | translate }}</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 py-1">
                    @for (tile of kindTiles; track tile.kind) {
                    <button type="button" class="kind-tile" [class.kind-tile--active]="parkingKind() === tile.kind"
                        [attr.aria-pressed]="parkingKind() === tile.kind" (click)="setParkingKind(tile.kind)">
                        <mat-icon>{{ tile.icon }}</mat-icon>
                        <span class="kind-tile__label">{{ tile.label }}</span>
                        <span class="kind-tile__desc">{{ tile.desc }}</span>
                    </button>
                    }
                </div>

                <!-- Where-to-add tips: Pool and Easypass are created elsewhere. -->
                <div class="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <div class="flex items-start gap-2">
                        <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-blue-600 mt-0.5">lightbulb</mat-icon>
                        <div class="text-sm text-blue-900">
                            <p class="font-medium mb-1">{{ 'parkingRight.add.tipsTitle' | translate }}</p>
                            <ul class="list-disc list-inside space-y-0.5 text-blue-800">
                                <li>{{ 'parkingRight.add.poolTip' | translate }}</li>
                                <li>{{ 'parkingRight.add.easypassTip' | translate }}</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Step 1 has no Back button (first screen). -->
                <div class="flex justify-end mt-3">
                    <jjames-button variant="primary" matIconRight="arrow_forward"
                        (clicked)="stepper.next()">Next</jjames-button>
                </div>
            </mat-step>

            <!-- ==================== STEP 2 — DETAILS ==================== -->
            <mat-step [completed]="isDetailsValid">
                <ng-template matStepLabel>Details</ng-template>

                <form [formGroup]="form" class="space-y-3 py-1">
                    @switch (parkingKind()) {

                    <!-- ---------- Long-Term / Supplier (Dauerparker) ---------- -->
                    @case ('LongTerm') {
                    @if (isLoadingLongTermTypes()) {
                    <div class="flex items-center justify-center py-8"><mat-spinner diameter="36"></mat-spinner></div>
                    } @else if (longTermTypes().length === 0) {
                    <div class="empty-state">
                        <mat-icon>event_repeat</mat-icon>
                        <p>No Long-Term type exists for this space yet.</p>
                        <jjames-button variant="primary" size="sm" [loading]="isCreatingLongTermType()"
                            (clicked)="createLongTermTypeInline()">Create Long-Term type</jjames-button>
                    </div>
                    } @else {
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Zones</mat-label>
                        <mat-select formControlName="selectedZones" multiple>
                            @for (zone of availableZones; track zone.id) {
                            <mat-option [value]="zone.id">{{ zone.name }}</mat-option>
                            }
                        </mat-select>
                        <mat-hint>Restrict the right to specific zones — leave empty for the whole space</mat-hint>
                    </mat-form-field>

                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Name</mat-label>
                        <input matInput formControlName="name" placeholder="e.g. Acme GmbH" />
                    </mat-form-field>

                    <mat-checkbox formControlName="validToEnabled" color="primary">Set an end date</mat-checkbox>
                    @if (form.get('validToEnabled')?.value) {
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Valid To</mat-label>
                        <input matInput [matDatepicker]="ltValidTo" formControlName="validTo">
                        <mat-datepicker-toggle matIconSuffix [for]="ltValidTo"></mat-datepicker-toggle>
                        <mat-datepicker #ltValidTo></mat-datepicker>
                    </mat-form-field>
                    }
                    }
                    }

                    <!-- ---------- Assigned Zone (default) ---------- -->
                    @default {
                    @if (isLoadingTypes()) {
                    <div class="flex items-center justify-center py-8"><mat-spinner diameter="36"></mat-spinner></div>
                    } @else if (assignedZoneTypes().length === 0) {
                    <div class="empty-state">
                        <mat-icon>info</mat-icon>
                        <p>No assigned zone type exists for this space yet.</p>
                    </div>
                    } @else {
                    <!-- Parking type is inferred from the selected kind and auto-selected; the old
                         "Assigned Zone Parking Type" dropdown is obsolete and intentionally hidden. -->
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Parking Zones</mat-label>
                        <mat-select formControlName="selectedZones" multiple required>
                            @for (zone of availableZones; track zone.id) {
                            <mat-option [value]="zone.id">{{ zone.name }}</mat-option>
                            }
                        </mat-select>
                        @if (availableZones.length === 0) {
                        <mat-hint class="text-orange-600">No zones for this space</mat-hint>
                        }
                    </mat-form-field>

                    <!-- Advanced: validity / schedule / duration -->
                    <mat-expansion-panel class="advanced-panel">
                        <mat-expansion-panel-header>
                            <mat-panel-title class="flex items-center gap-2">
                                <mat-icon>tune</mat-icon> Advanced (validity &amp; schedule)
                            </mat-panel-title>
                        </mat-expansion-panel-header>

                        <div class="space-y-3 pt-2">
                            <mat-form-field appearance="fill" class="w-full">
                                <mat-label>Right Type</mat-label>
                                <mat-select formControlName="rightType">
                                    @for (option of rightTypeOptions; track option.value) {
                                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                                    }
                                </mat-select>
                            </mat-form-field>

                            <div class="grid grid-cols-2 gap-4">
                                <mat-form-field appearance="fill">
                                    <mat-label>Valid From</mat-label>
                                    <input matInput [matDatepicker]="vf" formControlName="validFrom">
                                    <mat-datepicker-toggle matIconSuffix [for]="vf"></mat-datepicker-toggle>
                                    <mat-datepicker #vf></mat-datepicker>
                                </mat-form-field>
                                <mat-form-field appearance="fill">
                                    <mat-label>Valid To</mat-label>
                                    <input matInput [matDatepicker]="vt" formControlName="validTo">
                                    <mat-datepicker-toggle matIconSuffix [for]="vt"></mat-datepicker-toggle>
                                    <mat-datepicker #vt></mat-datepicker>
                                </mat-form-field>
                            </div>

                            @if (isTimeBased) {
                            <mat-checkbox formControlName="isAllDay" color="primary">All Day Access</mat-checkbox>
                            @if (!form.get('isAllDay')?.value) {
                            <div class="grid grid-cols-2 gap-4">
                                <mat-form-field appearance="fill">
                                    <mat-label>Start Time</mat-label>
                                    <input matInput type="time" formControlName="startTime">
                                </mat-form-field>
                                <mat-form-field appearance="fill">
                                    <mat-label>End Time</mat-label>
                                    <input matInput type="time" formControlName="endTime">
                                </mat-form-field>
                            </div>
                            }
                            <mat-form-field appearance="fill" class="w-full">
                                <mat-label>Valid Days</mat-label>
                                <mat-select formControlName="validDays" multiple>
                                    @for (day of daysOfWeek; track day.value) {
                                    <mat-option [value]="day.value">{{ day.label }}</mat-option>
                                    }
                                </mat-select>
                                <mat-hint>Leave empty for all days</mat-hint>
                            </mat-form-field>
                            <mat-checkbox formControlName="isRecurring" color="primary">Recurring (repeats
                                weekly)</mat-checkbox>
                            }

                            @if (isDurationBased) {
                            <mat-form-field appearance="fill" class="w-full">
                                <mat-label>Duration (minutes)</mat-label>
                                <input matInput type="number" formControlName="durationInMinutes" min="1">
                            </mat-form-field>
                            <mat-checkbox formControlName="isRenewable" color="primary">Allow Renewal</mat-checkbox>
                            @if (form.get('isRenewable')?.value) {
                            <mat-form-field appearance="fill" class="w-full">
                                <mat-label>Max Renewals</mat-label>
                                <input matInput type="number" formControlName="maxRenewals" min="1">
                            </mat-form-field>
                            }
                            }
                        </div>
                    </mat-expansion-panel>
                    }
                    }
                    }
                </form>

                <div class="flex justify-between mt-3">
                    <jjames-button variant="secondary" matIconLeft="arrow_back"
                        (clicked)="stepper.previous()">Back</jjames-button>
                    <jjames-button variant="primary" matIconRight="arrow_forward" [disabled]="!isDetailsValid"
                        (clicked)="stepper.next()">Next</jjames-button>
                </div>
            </mat-step>

            <!-- ==================== STEP 3 — PLATES & CONFIRM ==================== -->
            <mat-step [completed]="isPlatesValid">
                <ng-template matStepLabel>Plates</ng-template>

                <form [formGroup]="form" class="space-y-3 py-1">
                    <label class="block text-sm font-medium text-gray-700">License Plate</label>

                    <!-- Single plate input — hidden once the plate has been added -->
                    @if (licensePlates().length === 0) {
                    <div class="flex gap-2 items-start">
                        <mat-form-field appearance="fill" class="flex-1">
                            <mat-label>License Plate Number</mat-label>
                            <input matInput formControlName="licensePlateInput" placeholder="Enter license plate"
                                (keyup.enter)="addLicensePlate()" [disabled]="isAddingPlate()">
                        </mat-form-field>
                        <button mat-raised-button color="primary" type="button" (click)="addLicensePlate()"
                            [disabled]="isAddingPlate()" class="mt-1" aria-label="Add license plate">
                            @if (isAddingPlate()) {
                            <mat-spinner diameter="20"></mat-spinner>
                            } @else {
                            <ng-container><mat-icon>add</mat-icon> Set</ng-container>
                            }
                        </button>
                    </div>
                    }

                    <!-- Added plate -->
                    @if (licensePlates().length > 0) {
                    <div class="bg-gray-50 rounded-lg p-3">
                        <div class="text-sm text-gray-600 mb-2">License plate:</div>
                        <mat-chip-set>
                            @for (plate of licensePlates(); track plate.id) {
                            <mat-chip (removed)="removeLicensePlate(plate)">
                                {{ plate.fullNumber }}
                                <button matChipRemove aria-label="Remove license plate {{ plate.fullNumber }}">
                                    <mat-icon>cancel</mat-icon>
                                </button>
                            </mat-chip>
                            }
                        </mat-chip-set>
                    </div>
                    } @else {
                    <div class="text-sm text-orange-600 flex items-center gap-1">
                        <mat-icon class="text-base">warning</mat-icon>
                        At least one license plate is required
                    </div>
                    }

                    <!-- Comment (generic — available for all right kinds) -->
                    <mat-form-field appearance="fill" class="w-full">
                        <mat-label>Comment (optional)</mat-label>
                        <textarea matInput formControlName="comment" rows="2"
                            placeholder="Notes, reference numbers, etc."></textarea>
                    </mat-form-field>

                    <!-- Review summary -->
                    <div class="review-card">
                        <div class="review-row"><span>Kind</span><strong>{{ selectedKindLabel }}</strong></div>
                        <div class="review-row"><span>Type / Supplier</span><strong>{{ reviewTypeName }}</strong></div>
                        <div class="review-row"><span>Zones</span><strong>{{ reviewZoneNames }}</strong></div>
                        <div class="review-row"><span>Plate(s)</span><strong>{{ reviewPlates || '—' }}</strong></div>
                    </div>
                </form>

                <div class="flex justify-between mt-3">
                    <jjames-button variant="secondary" matIconLeft="arrow_back"
                        (clicked)="stepper.previous()">Back</jjames-button>
                    <jjames-button variant="primary" matIconLeft="add" [disabled]="!canCreate" [loading]="isLoading()"
                        (clicked)="onSubmit()">Create right</jjames-button>
                </div>
            </mat-step>

        </mat-stepper>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="gap-2">
        <jjames-button [variant]="'secondary'" (clicked)="onCancel()">
            Cancel
        </jjames-button>
    </mat-dialog-actions>
</div>
